import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import LoadingScreen from "@/components/shared/LoadingScreen";
import { useMutedAutoplayRetry } from "./useMutedAutoplayRetry";
import { useTapHandlers } from "./useTapHandlers";

interface StreamedVideoViewerProps {
  url: string;
  className?: string;
  /** Fires with the element's MediaError (null when unavailable). */
  onError?: (mediaError: MediaError | null) => void;
  /**
   * Extra line shown under the spinner while buffering — e.g. the receiver's
   * "Receiving video… 37%" while the sender is still uploading, so a long
   * buffer reads as progress rather than a hang.
   */
  bufferingLabel?: string;
  /** Hands the mounted <video> element up for the custom viewer controls. */
  onVideoElement?: (el: HTMLVideoElement | null) => void;
  /** A genuine tap on the video surface (drag-tails filtered out). */
  onTap?: () => void;
  /** Cached thumbnail shown until the first frame is decodable (see VideoViewer). */
  poster?: string;
}

export default function StreamedVideoViewer({
  url,
  className,
  onError,
  bufferingLabel,
  onVideoElement,
  onTap,
  poster,
}: StreamedVideoViewerProps) {
  const [isBuffering, setIsBuffering] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const tapHandlers = useTapHandlers(onTap);

  useEffect(() => {
    onVideoElement?.(videoRef.current);
    return () => onVideoElement?.(null);
  }, [onVideoElement]);

  // WebKit's media loader (AVFoundation) can keep its download session alive
  // after React unmounts the element — observed as an endless storm of range
  // requests against /sw-stream after a cancelled upload (each answered 410,
  // instantly retried). Explicitly detaching the source and forcing load() is
  // the documented way to tear the session down.
  //
  // The effect BODY re-attaches a stripped src: React 18 StrictMode (dev) runs
  // setup→cleanup→setup on the SAME DOM node, and after the cleanup strips the
  // attribute React never re-applies it (the vdom still records the unchanged
  // src prop) — the element parked at NETWORK_EMPTY: no error event, no error
  // card, autoplay retry hung forever. In production the src is present and
  // the body is a no-op.
  const urlRef = useRef(url);
  urlRef.current = url;
  useEffect(() => {
    const el = videoRef.current;
    if (el && !el.getAttribute("src") && urlRef.current) {
      try {
        el.setAttribute("src", urlRef.current);
      } catch {
        // jsdom lacks full media stubs; re-attach must never throw
      }
    }
    return () => {
      if (!el) {
        return;
      }
      try {
        // pause() first: the UA's pause-on-DOM-removal is an async
        // afterthought, so audio could outlive the unmount.
        el.pause();
        el.removeAttribute("src");
        el.load();
      } catch {
        // jsdom / older engines — nothing to tear down
      }
    };
  }, []);

  // Autoplay with sound where allowed; iOS falls back to muted (shared with the
  // small-video player so the iOS behaviour is identical).
  useMutedAutoplayRetry(videoRef, url);

  return (
    <div
      className={cn(
        // Explicit full-viewport frame (dvh, not vh: with iOS Safari's
        // toolbars visible 100vh overshoots the real viewport). Without it
        // this wrapper shrink-wrapped to the INVISIBLE buffering video's
        // intrinsic size (the poster, as small as a ~32px ThumbHash) inside
        // the zoom container — so the spinner rendered in a tiny box pinned
        // to the screen's top-left instead of centered, and the revealed
        // video sat top-left rather than centered.
        "relative flex h-dvh w-screen items-center justify-center",
        className,
      )}
    >
      <video
        ref={videoRef}
        src={url}
        // No native `controls` — the element is inside the zoom transform,
        // where native controls scale with the video and iOS stacks its glass
        // control overlay on top (duplicated controls). The viewer's custom
        // controls (outside the transform) drive playback instead.
        preload="metadata"
        playsInline
        poster={poster || undefined}
        // The source only resolves through THIS page's service worker: an
        // AirPlay target fetches the URL itself and gets nothing (black TV),
        // and a PiP window dies when the dialog's unmount teardown detaches
        // the src — so don't advertise either affordance.
        disableRemotePlayback
        disablePictureInPicture
        {...({ "x-webkit-airplay": "deny" } as Record<string, string>)}
        className={cn(
          // object-contain (not cover): show the whole frame, letterboxed —
          // cover cropped portrait/odd-ratio videos in fullscreen. FILLS the
          // frame above (one sizing rule with the poster), never sized by
          // intrinsic size (see wrapper note).
          "h-full w-full object-contain",
          isBuffering && "invisible",
        )}
        onCanPlay={() => setIsBuffering(false)}
        onPlaying={() => setIsBuffering(false)}
        onWaiting={() => setIsBuffering(true)}
        // Only a genuine element failure carries a MediaError (see VideoViewer).
        onError={(e) => {
          const mediaError = e.currentTarget.error;
          if (mediaError) {
            onError?.(mediaError);
          }
        }}
        {...tapHandlers}
      />
      {isBuffering && <LoadingScreen />}
      {isBuffering && bufferingLabel && (
        <div className="absolute inset-x-0 bottom-8 z-10 text-center text-sm text-muted-foreground">
          {bufferingLabel}
        </div>
      )}
    </div>
  );
}
